/**
 * Utility function to obtain a Google reCAPTCHA v3 score-based token.
 * 
 * @param {string} action - The action name for reCAPTCHA v3 (e.g. 'contact_us', 'booking_form', 'subscribe')
 * @returns {Promise<string|null>} Resolves with token string or null if site key is not configured / failed.
 */
export const getRecaptchaToken = (action = 'submit') => {
  return new Promise((resolve) => {
    const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
    if (!siteKey) {
      // If site key is not set in environment, resolve null so form submission continues cleanly
      return resolve(null);
    }

    if (typeof window === 'undefined') {
      return resolve(null);
    }

    const execute = () => {
      if (!window.grecaptcha) {
        return resolve(null);
      }
      window.grecaptcha.ready(() => {
        window.grecaptcha
          .execute(siteKey, { action })
          .then((token) => resolve(token))
          .catch((err) => {
            console.error('reCAPTCHA execution error:', err);
            resolve(null);
          });
      });
    };

    if (window.grecaptcha && typeof window.grecaptcha.execute === 'function') {
      execute();
    } else {
      const scriptId = 'google-recaptcha-v3-script';
      if (!document.getElementById(scriptId)) {
        const script = document.createElement('script');
        script.id = scriptId;
        script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
        script.async = true;
        script.onload = execute;
        script.onerror = (err) => {
          console.error('Failed to load reCAPTCHA script:', err);
          resolve(null);
        };
        document.head.appendChild(script);
      } else {
        // Script tag exists but hasn't finished loading yet, poll briefly
        let attempts = 0;
        const interval = setInterval(() => {
          attempts++;
          if (window.grecaptcha && typeof window.grecaptcha.execute === 'function') {
            clearInterval(interval);
            execute();
          } else if (attempts > 50) {
            clearInterval(interval);
            resolve(null);
          }
        }, 100);
      }
    }
  });
};
